import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import StoreBadgeButton from './StoreBadgeButton';
import { openGooglePlayListing } from '@utils/openGooglePlayListing';
import { openAppStoreListing } from '@utils/openAppStoreListing';

function isMobileUA(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '').toLowerCase();
  return /android|iphone|ipad|ipod|mobile/.test(ua);
}

/**
 * Banner para incentivar uso/instalação do app no mobile web.
 * Detecção de "app instalado" não é confiável na web; usamos tentativa de deep link + fallback.
 */
export const MobileAppNudgeBanner: React.FC<{
  /** Deep link para abrir o app (default: scheme root) */
  deepLinkUrl?: string;
}> = ({ deepLinkUrl = 'vover://' }) => {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem('mobileAppNudgeDismissed') === '1';
    } catch {
      return false;
    }
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [tryingOpen, setTryingOpen] = useState(false);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem('mobileAppNudgeDismissed', '1');
    } catch {
      // ignore
    }
  }, []);

  const handleTryOpenApp = useCallback(() => {
    setTryingOpen(true);
    const start = Date.now();

    const t = window.setTimeout(() => {
      setTryingOpen(false);
      // Se ainda estiver visível após tentativa, assume que não abriu.
      const stillVisible = document.visibilityState === 'visible' && Date.now() - start > 800;
      if (stillVisible) setModalOpen(true);
    }, 1100);

    // tentativa de abrir app
    window.location.href = deepLinkUrl;

    return () => window.clearTimeout(t);
  }, [deepLinkUrl]);

  if (!isMobileUA() || dismissed) return null;

  return (
    <>
      <Alert
        severity="info"
        onClose={handleDismiss}
        sx={{ mb: 2 }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button size="small" variant="contained" onClick={handleTryOpenApp} disabled={tryingOpen}>
              {tryingOpen ? 'Abrindo…' : 'Abrir no app'}
            </Button>
            <Button size="small" variant="outlined" onClick={() => setModalOpen(true)}>
              Instalar
            </Button>
          </Box>
        }
      >
        Para uma experiência melhor no celular, use o app Vover.
      </Alert>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Instalar o app</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Se você ainda não tem o app instalado, instale pela loja. Se já tem, use o botão “Abrir no app”.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <StoreBadgeButton
              type="google-play"
              onClick={() => openGooglePlayListing()}
              aria-label="Abrir no Google Play"
            />

            <StoreBadgeButton
              type="app-store"
              onClick={() => openAppStoreListing()}
              aria-label="Abrir na App Store"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MobileAppNudgeBanner;

